// Minimal WebAssembly binary reader: imports and exports with their function signatures.
// Enough for rtw-check to verify the v1 interface without instantiating anything.

export type ValType = 'i32' | 'i64' | 'f32' | 'f64' | 'v128' | 'ref' | 'other';
export interface FuncType { params: ValType[]; results: ValType[] }
export interface WasmImport { module: string; name: string; kind: 'func' | 'table' | 'memory' | 'global' | 'tag'; type?: FuncType; memory64?: boolean }
export interface WasmExport { name: string; kind: 'func' | 'table' | 'memory' | 'global' | 'tag'; type?: FuncType }
export interface WasmInfo { imports: WasmImport[]; exports: WasmExport[]; memories: { memory64: boolean }[] }

const KINDS = ['func', 'table', 'memory', 'global', 'tag'] as const;

function valType(b: number): ValType {
  switch (b) {
    case 0x7f: return 'i32';
    case 0x7e: return 'i64';
    case 0x7d: return 'f32';
    case 0x7c: return 'f64';
    case 0x7b: return 'v128';
    case 0x70: case 0x6f: return 'ref';
    default: return 'other';
  }
}

export function inspectWasm(bytes: Uint8Array): WasmInfo {
  let p = 0;
  const fail = (msg: string): never => { throw new Error(`wasm: ${msg} at byte ${p}`); };
  const u8 = () => (p < bytes.length ? bytes[p++] : fail('unexpected end'));
  const uleb = () => {
    let result = 0, shift = 0, b: number;
    do { b = u8(); result += (b & 0x7f) * 2 ** shift; shift += 7; } while (b & 0x80);
    return result;
  };
  const name = () => { const n = uleb(); const s = new TextDecoder().decode(bytes.subarray(p, p + n)); p += n; return s; };
  const limits = () => { const flag = u8(); uleb(); if (flag & 1) uleb(); return { memory64: (flag & 4) !== 0 }; };

  if (bytes.length < 8 || bytes[0] !== 0 || bytes[1] !== 0x61 || bytes[2] !== 0x73 || bytes[3] !== 0x6d) fail('bad magic');
  p = 8;
  const types: FuncType[] = [];
  const funcTypes: number[] = []; // type index of every function (imports first)
  const info: WasmInfo = { imports: [], exports: [], memories: [] };
  while (p < bytes.length) {
    const id = u8();
    const size = uleb();
    const end = p + size;
    if (id === 1) {
      const n = uleb();
      for (let i = 0; i < n; i++) {
        if (u8() !== 0x60) fail('unsupported type form');
        const np = uleb(); const params: ValType[] = [];
        for (let j = 0; j < np; j++) params.push(valType(u8()));
        const nr = uleb(); const results: ValType[] = [];
        for (let j = 0; j < nr; j++) results.push(valType(u8()));
        types.push({ params, results });
      }
    } else if (id === 2) {
      const n = uleb();
      for (let i = 0; i < n; i++) {
        const module = name(); const nm = name(); const k = u8();
        const imp: WasmImport = { module, name: nm, kind: KINDS[k] ?? fail('bad import kind') };
        if (k === 0) { const t = uleb(); funcTypes.push(t); imp.type = types[t]; }
        else if (k === 1) { u8(); limits(); }
        else if (k === 2) { imp.memory64 = limits().memory64; info.memories.push({ memory64: !!imp.memory64 }); }
        else if (k === 3) { u8(); u8(); }
        else if (k === 4) { u8(); uleb(); }
        info.imports.push(imp);
      }
    } else if (id === 3) {
      const n = uleb();
      for (let i = 0; i < n; i++) funcTypes.push(uleb());
    } else if (id === 5) {
      const n = uleb();
      for (let i = 0; i < n; i++) info.memories.push(limits());
    } else if (id === 7) {
      const n = uleb();
      for (let i = 0; i < n; i++) {
        const nm = name(); const k = u8(); const idx = uleb();
        const exp: WasmExport = { name: nm, kind: KINDS[k] ?? fail('bad export kind') };
        if (k === 0) exp.type = types[funcTypes[idx]];
        info.exports.push(exp);
      }
    }
    p = end;
  }
  return info;
}
